import { useCallback, useContext, useMemo } from "react";
import { Button } from "../general/Button/Button";
import styles from "../importer/ImporterPage.module.scss";
import { TransactionsPairForReconciliation } from "../../common/contracts/reconciliation.contract";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import { useReconcileTransactionsMutation } from "../../api/reconciliation.api";
import { FinancialAccount } from "../../common/types/financialAccount";
import { useToaster } from "../general/ToastMessages/useToaster";
import { ReconciliationContext } from "./Reconciliation.context";
import { getStatmentTransactionId } from "./helpers";

interface Props {
    pairs: TransactionsPairForReconciliation[];
    financialAccount: FinancialAccount;
    onHide: () => void;
    onBack: () => void;
    reconciles: boolean;
}

export const ReconciliationFooter: React.FC<Props> = ({
    onHide,
    onBack,
    pairs,
    financialAccount,
    reconciles,
}) => {
    const {
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIds,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
    } = useContext(ReconciliationContext);
    const requiringAcceptance = useMemo(
        () =>
            pairs.filter(
                (pair) =>
                    Boolean(
                        !pair.fromAccount &&
                            pair.fromStatement &&
                            !statementTransactionsToAddIds.includes(
                                getStatmentTransactionId(pair.fromStatement),
                            ),
                    ) ||
                    Boolean(
                        !pair.fromStatement &&
                            pair.fromAccount &&
                            !financialTransactionsToRemoveIds.includes(
                                pair.fromAccount.financialTransactionId,
                            ),
                    ),
            ),
        [
            pairs,
            financialTransactionsToRemoveIds,
            statementTransactionsToAddIds,
        ],
    );

    const acceptAll = useCallback(() => {
        requiringAcceptance.forEach((pair) => {
            if (pair.fromAccount) {
                registerFinancialTransactionToRemove(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (pair.fromStatement) {
                registerStatementTransactionToAdd(
                    getStatmentTransactionId(pair.fromStatement),
                );
            }
        });
    }, [
        requiringAcceptance,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
    ]);

    const toaster = useToaster();
    const reconcileMutation = useReconcileTransactionsMutation(() => {
        toaster.toast("Account reconciled successfully");
    });

    const reconcile = () => {
        const statementTransactionsToAdd = pairs
            .map((pair) => pair.fromStatement)
            .filter((fromStatement) => fromStatement !== undefined)
            .filter((fromStatement) =>
                statementTransactionsToAddIds.includes(
                    getStatmentTransactionId(fromStatement),
                ),
            )
            .map((fromStatement) => ({
                financialDocumentId: fromStatement.financialDocumentId,
                date: fromStatement.date,
                amount: fromStatement.amount,
                bankDescription: fromStatement.bankDescription,
            }));

        reconcileMutation.mutate({
            financialAccountId: financialAccount.id,
            financialTransactionsToRemove: financialTransactionsToRemoveIds,
            statementTransactionsToAdd,
        });
    };

    let cta: React.ReactNode;

    if (reconcileMutation.isSuccess) {
        cta = <Button onClick={onHide}>Close</Button>;
    } else if (requiringAcceptance.length === 0) {
        cta = (
            <ButtonWithLoader
                onClick={reconcile}
                loading={reconcileMutation.isPending}
                disabled={!reconciles}
            >
                Complete reconciliation
            </ButtonWithLoader>
        );
    } else {
        cta = <Button onClick={acceptAll}>Accept all</Button>;
    }

    return (
        <div className={styles.footer}>
            <Button variant="secondary" onClick={onBack}>
                Back
            </Button>
            {cta}
        </div>
    );
};
